import ModalComponent from 'components/Common/Modal';
import useOrganizationInfoDescriptionFunctions from 'components/Dashboard/hooks/useOrganizationInfoDescriptionFunctions';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { store } from 'components/Store';
import { useContext, useEffect, useRef, useState } from 'react';
import getAppSite from 'utils/getAppSite';
import time from 'utils/time';

const DashboardUpdateOrganisationDescription = ({ isOpenState }) => {
  const { isOpen, setIsOpen } = isOpenState ?? {};

  return (
    <>
      <ModalComponent
        maxWidth={850}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        overlayClassName={'react-modal-overlay modal-close-position-adjustment'}
        shouldCloseOnOverlayClick={false}
      >
        <UpdateOrganisationDescriptionContent isOpenState={isOpenState} />
      </ModalComponent>
    </>
  );
};

const UpdateOrganisationDescriptionContent = ({ isOpenState, notInitialDescription }) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan, team } = state;
  const { code, teamName } = activePlan;

  const { isOpen, setIsOpen } = isOpenState ?? {};

  const [loadingUpdateDescription, setLoadingUpdateDescription] = useState(false);
  const [initialDescription, setInitialDescription] = useState({});
  const [currentDescription, setCurrentDescription] = useState();

  const isParliamentarian = parliamentarianUsers(code);
  const { newAgencyPlan } = useTopicOrClientWord();
  const { removeAndUpdateOrganizationDescription, updateOrganizationInfoDescription, removeOrganizationDescription } =
    useOrganizationInfoDescriptionFunctions();

  const textAreaRef = useRef(null);

  useEffect(() => {
    setInitialDescription({
      authorFullName: team.organisationDescriptionAuthorFullName,
      lastModified: team.organisationDescriptionLastModified,
      organisationDescription: team?.organisationDescription,
    });
    setCurrentDescription(team?.organisationDescription);
  }, [team]);

  useEffect(() => {
    if (isOpen && textAreaRef.current) {
      const focusAtBeginning = () => {
        const textarea = textAreaRef.current;
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(0, 0);
          textarea.scrollTop = 0;
        } else {
          requestAnimationFrame(focusAtBeginning);
        }
      };
      setTimeout(focusAtBeginning, 300);
    }
  }, [isOpen]);

  const descriptionIsChanged =
    currentDescription !== initialDescription?.organisationDescription &&
    !(currentDescription === '' && initialDescription === null);

  const handleClick = async () => {
    try {
      setLoadingUpdateDescription(true);
      const text = currentDescription;
      const remove = text?.length === 0;
      let response;
      if (remove) {
        response = await removeOrganizationDescription({
          value: text,
        });
      } else {
        if (initialDescription?.lastModified) {
          response = await removeAndUpdateOrganizationDescription({
            value: text,
          });
        } else {
          response = await updateOrganizationInfoDescription({
            value: text,
          });
        }
      }
      if (!!response) {
        if (remove) {
          createNotification('success', 'Description has been removed');
        } else {
          createNotification(
            'success',
            `Description ${
              initialDescription?.authorFullName && initialDescription?.lastModified ? 'updated' : 'saved'
            }`
          );
        }
        setIsOpen(false);
      }
      setLoadingUpdateDescription(false);
    } catch (error) {}
  };

  const currentDescriptionValueValidator = currentDescription?.trim()?.length > 100;
  const typingTimeOut = useRef(null);

  return (
    <div className={`tailor-summaries-modal`}>
      <p className='popup-header px-3 px-lg-5 flex-centered'>
        <span>
          Tailor your AI summaries and briefings for <span>{teamName?.length ? teamName : ''}</span>
        </span>
      </p>

      <div className='separator' />
      <div className='popup-body p-3 px-lg-5 pb-lg-2'>
        <div>
          <p>
            Enter as much information as possible about {isParliamentarian ? '' : 'your'}{' '}
            {newAgencyPlan
              ? 'client and their work'
              : `${
                  isParliamentarian ? '' : `${getAppSite() === 'usa' ? 'organization' : 'organisation'} and your`
                } work`}
            . This text will be saved in order to improve the relevance of the AI-generated briefings and summaries of
            updates.
          </p>
          <div className='position-relative'>
            <textarea
              ref={textAreaRef}
              value={currentDescription}
              rows={7}
              className='input-empty w-100 border border-grey rounded-sm p-3'
              style={{ resize: 'none' }}
              onChange={(e) => {
                setCurrentDescription(e.target.value);
              }}
              onKeyDown={(e) => {
                if (typingTimeOut.current) clearTimeout(typingTimeOut?.current);
                typingTimeOut.current = setTimeout(() => {
                  if (e.target?.value?.trim()?.length < 100) {
                    createNotification('danger', 'Please enter a description that is at least 100 characters');
                  }
                }, 500);
              }}
            ></textarea>
          </div>
          <div
            className={`pb-3 tailor-summary-action-container flex-centered justify-content-${
              initialDescription?.lastModified ? 'between' : 'end'
            }  mt-3`}
          >
            <>
              {initialDescription?.lastModified && (
                <p className='mb-0 pt-3 last-edited-by-subtext'>
                  <i>
                    Last edited by {initialDescription?.authorFullName || 'PolicyMogul'} on{' '}
                    {time(initialDescription?.lastModified).formatLongDate(true)}
                  </i>
                </p>
              )}
            </>

            <div className='flex-centered pt-1'>
              <button
                className={`action-button general-button px-3 filter-action-button update-button`}
                onClick={() => {
                  handleClick();
                }}
                disabled={
                  !currentDescriptionValueValidator ||
                  (!descriptionIsChanged && !notInitialDescription?.current) ||
                  loadingUpdateDescription
                }
              >
                {loadingUpdateDescription && <i className='fas fa-spinner fa-spin mx-2 d-inline-block-centered' />}
                <span>
                  {initialDescription?.lastModified ? 'Update' : 'Save'} description
                  {newAgencyPlan ? ` for ${team?.teamName}` : ''}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className='mb-2 flex-centered justify-content-end'>
          <button className='general-button delete-button px-4 mr-3 py-1' onClick={() => setIsOpen(false)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export { UpdateOrganisationDescriptionContent };
export default DashboardUpdateOrganisationDescription;
